import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useArchiveStats } from "../../../hooks/useArchiving";
import type { ArchiveEntry } from "../../../server/archiving";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getArchiveStats } from "../../../server/archiving";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/archiving/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.archiving.stats(),
      queryFn: async () => {
        const result = await getArchiveStats({
          data: { accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={3} variant="cards" />,
  errorComponent: createRouteError("Archiving"),
  component: ArchivingPage,
});

function ArchivingPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedArchiving accessToken={session.access_token} />;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function AuthenticatedArchiving({ accessToken }: { accessToken: string }) {
  const { data: stats, isLoading, error } = useArchiveStats({ accessToken });

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Data Archiving</h1>
            <p className="text-muted-foreground">
              Manage archived files and storage optimization
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        {isLoading && (
          <p className="text-muted-foreground">Loading archive data...</p>
        )}
        {error && (
          <p className="text-destructive">Error: {error.message}</p>
        )}

        {!isLoading && !error && stats && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground">Total Archived</p>
                  <p className="text-2xl font-semibold">{stats.totalArchived} files</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground">Total Size</p>
                  <p className="text-2xl font-semibold">{formatBytes(stats.totalSizeBytes)}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Archives</CardTitle>
                <CardDescription>Most recently archived files</CardDescription>
              </CardHeader>
              <CardContent>
                {stats.recentArchives.length === 0 && (
                  <p className="text-muted-foreground">No archived files yet.</p>
                )}
                {stats.recentArchives.length > 0 && (
                  <ul className="space-y-2">
                    {stats.recentArchives.map((a: ArchiveEntry) => (
                      <li
                        key={a.id}
                        className="flex items-center justify-between rounded-lg border bg-card p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{a.original_file_path}</p>
                          <p className="text-xs text-muted-foreground">
                            {a.mime_type ?? "unknown"} · {formatBytes(a.file_size_bytes)} ·{" "}
                            Archived {new Date(a.archived_at).toLocaleDateString()}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
